"use client";

import Button from "@marginly/ui/components/button";
import { FieldInnerContainer, Superfield } from "@marginly/ui/components/input";
import Label from "@marginly/ui/components/label";
import T from "@marginly/ui/components/typography";
import BigNumber from "bignumber.js";
import cn from "classnames";
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "ttag";
import { useChainId } from "wagmi";

import Space from "@/components/common/space";
import { TokenIconAdaptive } from "@/components/common/token/token-icon/styled";
import { AssetItem } from "@/components/navigation/wallet-modal/styled";
import RangeSlider from "@/components/ui/range-slider";
import { Noop } from "@/components/util";
import { CHAINID_MAP } from "@/constants/chains";
import { NBSP } from "@/constants/common";
import { ZERO } from "@/constants/math";
import { useClickOutside } from "@/hooks/common";
import { TokenDetails } from "@/hooks/token";
import { FetchBalanceResult } from "@/types/common";
import { transformToAmount } from "@/util/format";
import { formatCoin } from "@/util/format-coin";

import { TokenImage } from "../../common/token/token-image";
import type { PositionDirection, TradeToken } from "../state";
import {
  Dropdown,
  DropdownBody,
  LayerFix,
  MinusIcon,
  PlusIcon,
} from "./styled";

interface TokenSelectProps {
  token?: TradeToken;
  unwrap?: TradeToken;
  base?: TokenDetails;
  quote?: TokenDetails;
  native?: Omit<TokenDetails, "address">;
  baseBalance?: FetchBalanceResult;
  quoteBalance?: FetchBalanceResult;
  onTokenChange?: (token: TradeToken) => void;
}

const TokenSelect = ({
  token,
  unwrap,
  base,
  baseBalance,
  quote,
  quoteBalance,
  native,
  onTokenChange,
}: TokenSelectProps) => {
  const baseSymbol = (unwrap === "base" ? native : base)?.symbol;
  const quoteSymbol = (unwrap === "quote" ? native : quote)?.symbol;
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, setIsDropdownVisible.bind(null, false));
  const tokenSymbol = token && { baseSymbol, quoteSymbol }[`${token}Symbol`];
  return (
    <Dropdown ref={ref}>
      <Button
        md
        elevated
        icon={<TokenIconAdaptive token={tokenSymbol || ""} />}
        className={cn("counter")}
        style={{ padding: "4px" }}
        onClick={setIsDropdownVisible.bind(null, (v) => !v)}
      />

      {isDropdownVisible ? (
        <DropdownBody>
          <AssetItem
            onClick={() => {
              onTokenChange?.("base");
              setIsDropdownVisible(false);
            }}
          >
            <TokenImage token={baseSymbol} rectangle />

            <div>
              <T body>{baseSymbol}</T>
              <T secondary caption>
                {formatCoin(baseBalance?.formatted)} in wallet
              </T>
            </div>

            {token === "base" ? (
              <img
                src="/images/check.svg"
                alt=""
                style={{
                  width: "24px",
                  height: "24px",
                  marginLeft: "auto",
                  filter: "invert(var(--svg-invert)) ",
                  display: "block",
                }}
                className="checkmark"
              />
            ) : (
              <Noop />
            )}
          </AssetItem>

          <Space height={1} heightMobile={1} />

          <AssetItem
            onClick={() => {
              onTokenChange?.("quote");
              setIsDropdownVisible(false);
            }}
          >
            <TokenImage token={quoteSymbol} rectangle />

            <div>
              <T body>{quoteSymbol}</T>
              <T secondary caption>
                {formatCoin(quoteBalance?.formatted)} in wallet
              </T>
            </div>

            {token === "quote" ? (
              <img
                src="/images/check.svg"
                alt=""
                style={{
                  width: "24px",
                  height: "24px",
                  marginLeft: "auto",
                  filter: "invert(var(--svg-invert)) ",
                  display: "block",
                }}
                className="checkmark"
              />
            ) : (
              <Noop />
            )}
          </AssetItem>
        </DropdownBody>
      ) : (
        <Noop />
      )}
    </Dropdown>
  );
};

interface Props {
  amount?: string;
  direction?: PositionDirection;
  error?: boolean;
  leverage?: number;
  maxLeverage: number;
  symbol?: string;
  token?: TradeToken;
  unwrap?: TradeToken;
  onChange?: (values: {
    amount?: string;
    leverage?: number;
    direction?: PositionDirection;
  }) => void;
  onTokenChange?: (token: TradeToken) => void;
  base?: TokenDetails;
  quote?: TokenDetails;
  baseBalance?: FetchBalanceResult;
  quoteBalance?: FetchBalanceResult;
  position?: BigNumber;
  isCritical?: boolean;
  defaultLeverage: number;
  flip?: boolean;
  children?: ReactNode;
  minPositionSize?: BigNumber;
}

const InputPartial = ({
  amount,
  error,
  leverage,
  maxLeverage,
  defaultLeverage,
  onChange,
  onTokenChange,
  minPositionSize,
  base,
  quote,
  token,
  unwrap,
  baseBalance,
  quoteBalance,
  position,
  isCritical,
  children,
  flip,
}: Props) => {
  const chainId = useChainId();
  const native = CHAINID_MAP[chainId]?.nativeCurrency;
  const symbol = (unwrap === token ? native : { base, quote }[token!])?.symbol;
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLLabelElement>(null);
  useClickOutside(inputRef, () => setInputFocused(false));

  const handleDepositAmountButtons = useCallback(
    (delta: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setInputFocused(true);

      if (typeof inputRef !== "function") {
        inputRef?.current?.focus();
      }

      const num = Number(amount);
      const nextAmount =
        Number.isFinite(num) && num
          ? ZERO.plus(num).plus(delta)
          : ZERO.plus(delta);

      onChange?.({
        amount: nextAmount.lte(ZERO)
          ? ZERO.toString(10)
          : nextAmount.toString(10),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amount, onChange],
  );

  const suggest = useMemo(() => {
    const suggest = new Set<string>();

    const balance = !token
      ? 0
      : { baseBalance, quoteBalance }[`${token}Balance`]?.formatted;

    const bNum = Number(balance);
    const min = position?.gt(0)
      ? minPositionSize?.gt(position)
        ? minPositionSize
        : position
      : minPositionSize;

    if (min) {
      suggest.add(min.toString(10));
    }

    if (isFinite(bNum) && bNum > 0) {
      const max = (position ?? ZERO).plus(balance!);

      if (max.gt(min ?? 0)) {
        suggest.add(max.toString(10));
      }
    }

    return Array.from(suggest).sort((a, b) => Number(a) - Number(b));
  }, [position, quoteBalance, baseBalance, token, minPositionSize]);

  return (
    <>
      <Space height={32} heightMobile={32} />

      <LayerFix>
        <Superfield
          error={error}
          ref={inputRef}
          filled={Boolean(amount)}
          focused={inputFocused}
          placeholder={t`Your margin`}
          value={amount}
          onClick={(e) => {
            e.stopPropagation();
            setInputFocused(true);
          }}
          onChange={(e) =>
            onChange?.({
              amount: transformToAmount(e.target.value),
            })
          }
          postfix={symbol}
          title={amount ? "Your margin" : "Enter amount to deposit"}
          style={{ zIndex: 9999 }}
        >
          <FieldInnerContainer controls>
            {flip ? (
              <TokenSelect
                token={token}
                unwrap={unwrap}
                base={base}
                quote={quote}
                native={native}
                baseBalance={baseBalance}
                quoteBalance={quoteBalance}
                onTokenChange={onTokenChange}
              />
            ) : (
              <>
                <Button
                  md
                  elevated
                  icon
                  className={cn("counter", { disabled: !amount })}
                  onClick={handleDepositAmountButtons(-0.1)}
                  style={{ padding: "4px" }}
                >
                  <MinusIcon />
                </Button>
                <Button
                  md
                  elevated
                  icon
                  className="counter"
                  onClick={handleDepositAmountButtons(0.1)}
                  style={{ padding: "4px" }}
                >
                  <PlusIcon />
                </Button>
              </>
            )}
          </FieldInnerContainer>
          <FieldInnerContainer suggestion>
            {suggest.map((val, i) => (
              <Label
                key={val}
                badge
                onClick={onChange?.bind(null, {
                  amount: val,
                })}
              >
                <div>
                  {i === 0
                    ? `Min${NBSP}`
                    : i === suggest.length - 1
                    ? `Max${NBSP}`
                    : ""}
                  &nbsp;
                  {formatCoin(val) || 0}&nbsp;
                  {symbol}
                </div>
              </Label>
            ))}
          </FieldInnerContainer>
        </Superfield>
      </LayerFix>

      {children}

      <Space height={32} heightMobile={32} />

      <RangeSlider
        className={inputFocused ? "not-focused" : ""}
        title={"Leverage"}
        max={maxLeverage}
        value={leverage ?? defaultLeverage}
        handleChange={(e) => onChange?.({ leverage: Number(e.target.value) })}
        onTouchStart={() => setInputFocused(false)}
        isCritical={isCritical}
      />
    </>
  );
};

export default InputPartial;
